/**
 * Chart timeframe helpers — the single source of truth for the window/bucket
 * model used by `ConnectedChartContainer` and the SSR/hover prefetch helper.
 *
 * The user selects a TIMEFRAME (a lookback window: 1H, 6H, 1D, 1W, 1M, All);
 * the backend BUCKET is chosen by the API from that window and the measured
 * plot density. The lookback never predates trading start.
 *
 * `@morfi/chart` owns the canvas + theme + formatting; only this timeframe math
 * lives here.
 */

import type { ChartInterval, ConcreteChartInterval } from './types';

const WITHOUT_LIVE_INTERVAL_CACHE: WeakMap<
  ReadonlySet<ChartInterval>,
  ReadonlySet<ChartInterval>
> = new WeakMap();

const HISTORICAL_INTERVAL_ORDER: readonly ChartInterval[] = ['1h', '6h', '1d', '1w', '1mo', 'all'];

/** The selectable timeframes that map to a fixed lookback window — everything
 *  except the two virtual ones (`'live'` streams ticks; `'all'` spans the whole
 *  lifespan). */
export type WindowedTimeframe = Exclude<ChartInterval, 'live' | 'all'>;

/**
 * Single source of truth for the windowed lookback duration. CAGG selection
 * belongs to the backend's adaptive `interval=all` plan.
 *
 * | Timeframe | Window |
 * |-----------|--------|
 * | 1h        | 1 hour |
 * | 6h        | 6 h    |
 * | 1d        | 1 day  |
 * | 1w        | 7 days |
 * | 1mo       | 30 days|
 *
 * These are lookback controls, not resolution controls.
 */
export const TIMEFRAME_CONFIG: Readonly<Record<WindowedTimeframe, { windowSeconds: number }>> = {
  '1h': { windowSeconds: 3600 },
  '6h': { windowSeconds: 21600 },
  '1d': { windowSeconds: 86400 },
  '1w': { windowSeconds: 604800 },
  '1mo': { windowSeconds: 2592000 },
};

/** Visible window for the `'live'` tick stream, in seconds. */
export const LIVE_WINDOW_SECONDS = 60;

/** Historical TWAR seed needed to anchor the live consensus line. */
export const LIVE_CHART_SEED_SECONDS: number = LIVE_WINDOW_SECONDS * 3;

/**
 * `all` has a stable trading-start `from` and no rolling `to`, so realtime
 * rows may advance its visible wall-clock anchor without changing query
 * identity. Every concrete interval belongs to a bounded historical request;
 * its anchor must stay frozen while NATS patches the open bucket.
 */
export function chartAnchorFollowsRealtime(
  interval: ConcreteChartInterval | 'all',
): interval is 'all' {
  return interval === 'all';
}

/**
 * Exclusive API right edge for a visible chart anchor.
 *
 * Windowed charts keep a minute-stable cache key by rounding their visible
 * anchor down. The API query is half-open, so its `to` must be the next bucket
 * boundary or the bucket beginning at the visible anchor is excluded. `all`
 * is adaptive, so it advances to the next minute boundary; that is sufficient
 * to include the current sparse bucket at every supported CAGG resolution.
 */
export function exclusiveChartRequestEnd(anchorMs: number): string {
  return new Date(Math.floor(anchorMs / 60_000) * 60_000 + 60_000).toISOString();
}

/**
 * Lookback window (seconds) the canvas should draw for a timeframe.
 *  - `'live'` → 30 s.
 *  - `'all'` → the full trading span (trading start→anchor), floored at 60 s so a
 *    same-second-old market doesn't produce a degenerate 0-width window.
 *  - windowed timeframes → the fixed `TIMEFRAME_CONFIG` window, clamped to the
 *    lifespan so the lookback never predates creation (never-empty guarantee).
 */
export function windowSecondsForTimeframe(
  interval: ChartInterval,
  lifespanSeconds: number,
): number {
  const lifespan = Math.max(0, lifespanSeconds);
  if (interval === 'live') return LIVE_WINDOW_SECONDS;
  if (interval === 'all') return Math.max(60, lifespan);
  return Math.min(TIMEFRAME_CONFIG[interval].windowSeconds, Math.max(60, lifespan));
}

/**
 * Timeframes to expose in the selector for a market of the given lifespan
 * (trading start→now, or trading start→resolution for a resolved market).
 *
 * `'live'` and `'all'` are always available. A windowed timeframe appears only
 * when its window fits inside the market's lifetime — so its lookback never
 * predates creation and the canvas is never padded with empty space. (Product
 * decision: hide windows larger than the market's age.)
 *
 * Example: a 45-minute-old market shows `{live, all}`; a 3-day-old market shows
 * `{live, 1h, 6h, 1d, all}`; a 6-month-old market shows everything.
 */
export function getAvailableIntervals(lifespanSeconds: number): ReadonlySet<ChartInterval> {
  const lifespan = Math.max(0, lifespanSeconds);
  const available = new Set<ChartInterval>(['live', 'all']);
  const windowed: WindowedTimeframe[] = ['1h', '6h', '1d', '1w', '1mo'];
  for (const tf of windowed) {
    if (TIMEFRAME_CONFIG[tf].windowSeconds <= lifespan) available.add(tf);
  }
  return available;
}

/** Resolve a cross-chart preference without overwriting it. Live is a
 * streaming mode, so historical selections fall back to the nearest
 * historical option, preferring the shorter window when distances tie. */
export function resolveAvailableInterval(
  interval: ChartInterval,
  availableIntervals: ReadonlySet<ChartInterval> | undefined,
): ChartInterval {
  if (availableIntervals === undefined || availableIntervals.has(interval)) return interval;

  const selectedIndex = HISTORICAL_INTERVAL_ORDER.indexOf(interval);
  if (selectedIndex >= 0) {
    for (let distance = 1; distance < HISTORICAL_INTERVAL_ORDER.length; distance += 1) {
      const shorter = HISTORICAL_INTERVAL_ORDER[selectedIndex - distance];
      if (shorter !== undefined && availableIntervals.has(shorter)) return shorter;

      const longer = HISTORICAL_INTERVAL_ORDER[selectedIndex + distance];
      if (longer !== undefined && availableIntervals.has(longer)) return longer;
    }
  }

  return availableIntervals.has('all') ? 'all' : 'live';
}

/**
 * Candle charts need bucketed OHLCV data, so the tick-only `live` interval is
 * unavailable. Cache the derived set by its source identity: consumers place
 * it in effect dependencies, where recreating an equivalent `Set` would
 * retrigger their work after every render.
 */
export function excludeLiveInterval(
  intervals: ReadonlySet<ChartInterval>,
): ReadonlySet<ChartInterval> {
  if (!intervals.has('live')) return intervals;

  const cached = WITHOUT_LIVE_INTERVAL_CACHE.get(intervals);
  if (cached !== undefined) return cached;

  const withoutLive = new Set(intervals);
  withoutLive.delete('live');
  WITHOUT_LIVE_INTERVAL_CACHE.set(intervals, withoutLive);
  return withoutLive;
}
